import { type ChangeEvent, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { getDefaultVenueImageBySport, getVenuePrimarySport } from 'constants/venues';
import { useGetVenuesByGeoQuery } from 'types/generated/client';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { useApiGateway } from 'hooks/useApi';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useAddressSearch } from 'components/forms/AddressSearch/useAddressSearch';
import type { AddVenueProps, AddVenueResult, AddressProp } from './props';

const SEARCH_RADIUS_MILES = 20;
// The query can't order by distance, so everything in range is fetched and the nearest are shown.
const MAX_RESULTS = 40;

type Step = 'search' | 'manual' | 'manualPreview';

type Options = Pick<AddVenueProps, 'onSubmit' | 'onSubmitManualAddress'>;

export function useAddVenue({ onSubmit, onSubmitManualAddress }: Options) {
  const [step, setStep] = useState<Step>('search');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const isManual = step !== 'search';
  // Below `lg` the panes are steps of one sheet: picking a venue is what opens its preview.
  const isPreviewStep = step === 'manualPreview' || (!isManual && !!selectedSlug);

  const {
    position: viewerPosition,
    addressString: viewerAddress,
    isInitializing: isLocating,
  } = useGeoLocation();
  const venueSearch = useAddressSearch();
  const manualSearch = useAddressSearch();

  const center = venueSearch.position ?? viewerPosition;
  const { data: venuesData, loading: isVenuesLoading } = useGetVenuesByGeoQuery({
    variables: {
      distance: milesToMeters(SEARCH_RADIUS_MILES),
      from: { type: 'Point', coordinates: [center?.longitude ?? 0, center?.latitude ?? 0] },
    },
    skip: !center,
  });

  const venues = useMemo(() => {
    if (!center) return [];
    const distanceTo = (venue: NonNullable<typeof venuesData>['venues'][number]) =>
      calculateHaversineDistance({
        coord1: center,
        coord2: {
          latitude: venue.geometry.coordinates[1],
          longitude: venue.geometry.coordinates[0],
        },
        unit: 'miles',
      });
    return (venuesData?.venues ?? [])
      .map((venue) => ({ venue, distance: distanceTo(venue) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, MAX_RESULTS)
      .map(({ venue }) => venue);
  }, [venuesData, center]);

  const { get: fetchVenueBySlug } = useApiGateway<{}, AddVenueResult>('/v1/venues');
  const [selectedVenue, setSelectedVenue] = useState<AddVenueResult | null>(null);
  const latestSlug = useRef<string | null>(null);
  const isVenueLoading = !!selectedSlug && !selectedVenue;

  const selectVenue = async (slug: string) => {
    latestSlug.current = slug;
    setSelectedSlug(slug);
    setSelectedVenue(null);
    const response = await fetchVenueBySlug({ endpoint: slug });
    // Responses can land out of order; only the latest pick may write.
    if (latestSlug.current !== slug) return;
    const venue = response.data as AddVenueResult | undefined;
    if (response.isError || !venue) {
      toast.error(response.error || 'Could not load this location');
      setSelectedSlug(null);
      return;
    }
    setSelectedVenue(venue);
  };

  // Same source as the list card, so the preview reuses the image the browser already has.
  const selectedListVenue = venues.find((venue) => venue.slug === selectedSlug);
  const selectedImageUrl =
    selectedListVenue?.images?.[0]?.url ||
    selectedVenue?.images?.[0]?.url ||
    getDefaultVenueImageBySport(getVenuePrimarySport(selectedListVenue ?? selectedVenue ?? {}));

  const clearVenue = () => {
    latestSlug.current = null;
    setSelectedSlug(null);
    setSelectedVenue(null);
  };

  const manualAddress: AddressProp | null =
    manualSearch.position && manualSearch.addressString
      ? { address: manualSearch.addressString, ...manualSearch.position }
      : null;

  const handleManualInput = (e: ChangeEvent<HTMLInputElement>) => {
    // Typing invalidates the coordinates of whatever was picked before.
    manualSearch.setPosition(null);
    manualSearch.handleAutocompleteSuggestions(e);
  };

  const exitManualMode = () => {
    manualSearch.reset();
    setStep('search');
  };

  const goBack = () => {
    if (step === 'manualPreview') setStep('manual');
    else if (step === 'manual') exitManualMode();
    else clearVenue();
  };

  const canSubmit = isManual ? !!manualAddress : !!selectedVenue;

  const submit = () => {
    if (isManual && manualAddress) onSubmitManualAddress(manualAddress);
    if (!isManual && selectedVenue) onSubmit(selectedVenue);
  };

  return {
    isManual,
    isPreviewStep,
    showPreviewStep: () => setStep('manualPreview'),
    goBack,

    venueSearch,
    searchPlaceholder: viewerAddress || 'Search by location',
    venues,
    // Without a centre there is nothing to fetch: skeletons only while one is being resolved.
    isVenuesLoading: isVenuesLoading || (!center && isLocating),
    hasSearchCenter: !!center,
    selectedSlug,
    selectedVenue,
    selectedImageUrl,
    isVenueLoading,
    selectVenue,
    clearVenue,

    manualSearch,
    manualAddress,
    handleManualInput,
    enterManualMode: () => setStep('manual'),
    exitManualMode,

    canSubmit,
    submit,
  };
}

export type AddVenueState = ReturnType<typeof useAddVenue>;
